import React from 'react';
import { Button } from '@/components/UI/Button';

export const NextIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

export const BackIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
    <path d="M19 12H5" />
    <path d="m12 19-7-7 7-7" />
  </svg>
);

interface StepShellProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onBack?: () => void;
  onNext: () => void;
  onSkip?: () => void;
  saving?: boolean;
  error?: string;
  nextLabel?: string;
  /** When true, overlays the form fields so SAM can guide without the user accidentally editing. */
  disabled?: boolean;
}

/**
 * Shared card + footer chrome for guided-setup steps. Keeps every step body consistent
 * (card surface, title/subtitle, Back/Next, optional "Skip this step") so individual
 * step components only declare their own fields.
 */
export const StepShell: React.FC<StepShellProps> = ({
  title,
  subtitle,
  children,
  onBack,
  onNext,
  onSkip,
  saving = false,
  error,
  nextLabel = 'Next',
  disabled = false,
}) => (
  <div className="card-surface p-6 sm:p-8">
    <h2 className="text-h3 text-ui-text mb-1">{title}</h2>
    {subtitle && <p className="text-ui-text2 text-sm mb-6">{subtitle}</p>}

    <div className={disabled ? 'pointer-events-none opacity-60 select-none' : ''}>
      {children}
    </div>

    {error && (
      <p role="alert" className="text-danger-text text-sm mt-4 mb-1">
        {error}
      </p>
    )}

    <div className="flex gap-3 mt-6">
      {onBack && (
        <Button type="button" variant="outline" size="lg" onClick={onBack} disabled={disabled} className="flex-1">
          <BackIcon />
          Back
        </Button>
      )}
      <Button type="button" size="lg" onClick={onNext} disabled={saving || disabled} loading={saving} className="flex-1">
        {saving ? (
          'Saving...'
        ) : (
          <>
            {nextLabel}
            <NextIcon />
          </>
        )}
      </Button>
    </div>

    {onSkip && (
      <Button type="button" variant="link" onClick={onSkip} disabled={disabled} className="mt-3 w-full">
        Skip this step
      </Button>
    )}
  </div>
);
